import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { api } from '../services/api-client.js';
import { useAuthStore } from '../stores/auth-store.js';
import VehicleCard from '../components/VehicleCard.js';
import InstallBanner from '../components/InstallBanner.js';
import { useInstallPrompt } from '../hooks/useInstallPrompt.js';

function fmt(cents: number | null | undefined): string {
  if (cents == null) return '$0';
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function ConnectPlatformsCard() {
  const { data: platforms } = useQuery({
    queryKey: ['marketplace-platforms'],
    queryFn: () => api.get<any[]>('/marketplace/platforms'),
  });

  // Hide if platforms are connected or still loading
  const connected = platforms?.data ?? [];
  if (!platforms || connected.length > 0) return null;

  return (
    <Link
      to="/marketplace?tab=platforms"
      className="card p-5 mb-8 flex items-center gap-4 border border-surface-700 hover:border-brand-500 transition-colors"
    >
      <div className="w-10 h-10 rounded-full bg-brand-500/20 flex items-center justify-center shrink-0">
        <svg className="w-5 h-5 text-brand-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101M10.172 13.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      </div>
      <div className="flex-1">
        <p className="font-semibold text-sm">Auto-post to Facebook, Craigslist, and more</p>
        <p className="text-surface-500 text-xs mt-0.5">Connect your accounts to reach more buyers</p>
      </div>
      <svg className="w-5 h-5 text-surface-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  );
}

/** QR code — only shows on desktop screens */
function DesktopQR() {
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const { isMobile } = useInstallPrompt();

  useEffect(() => {
    if (isMobile) return; // Don't fetch token on mobile
    let cancelled = false;
    const fetchToken = () => {
      api.post<{ token: string }>('/auth/mobile-token').then((res) => {
        if (!cancelled && res.success) {
          setQrUrl(`${window.location.origin}/m?t=${res.data!.token}`);
        }
      });
    };
    fetchToken();
    const interval = setInterval(fetchToken, 25 * 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isMobile]);

  // Hide on mobile — install banner handles that UX
  if (isMobile || !qrUrl) return null;

  return (
    <div className="card p-6 mb-8 flex items-center gap-6">
      <div className="bg-white p-3 rounded-xl shrink-0">
        <QRCodeSVG value={qrUrl} size={120} level="M" />
      </div>
      <div>
        <h3 className="font-bold text-lg mb-1">Add a Vehicle</h3>
        <p className="text-surface-400 text-sm">Scan this QR code with your phone to scan a VIN, take photos, and list your vehicle.</p>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const dealership = useAuthStore((s) => s.dealership);
  const isDealer = useAuthStore((s) => s.isDealer);
  const dealer = isDealer();

  const { data: inventory, isLoading } = useQuery({
    queryKey: ['vehicles', 'recent'],
    queryFn: () => api.get<any[]>('/vehicles?limit=6'),
  });

  const { data: dealStats } = useQuery({
    queryKey: ['deals-stats'],
    queryFn: () => api.get<any>('/deals/stats/summary'),
    enabled: dealer,
  });

  const { data: customerStats } = useQuery({
    queryKey: ['customer-stats'],
    queryFn: () => api.get<any>('/customers/stats/summary'),
    enabled: dealer,
  });

  const vehicles = inventory?.data ?? [];
  const total = (inventory as any)?.pagination?.total ?? 0;
  const stats = dealStats?.data;
  const custStats = customerStats?.data;

  // Count active deals from pipeline
  const activeDeals = (stats?.by_status ?? [])
    .filter((s: any) => !['completed', 'lost', 'cancelled'].includes(s.status))
    .reduce((sum: number, s: any) => sum + s.count, 0);

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-surface-700 rounded w-48" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-28 bg-surface-700 rounded-xl" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-48 bg-surface-700 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      {/* Hero */}
      <div className="py-8">
        <h2 className="text-2xl font-bold">Welcome back</h2>
        <p className="text-surface-500 mt-1">{dealership?.name}</p>
      </div>

      {/* Mobile: Install Banner | Desktop: QR Code */}
      <InstallBanner />
      <DesktopQR />

      {/* Connect Platforms — show when no platforms connected */}
      <ConnectPlatformsCard />

      {/* Quick Stats */}
      {dealer ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-brand-400">{total}</p>
            <p className="text-xs text-surface-500 mt-1">Vehicles</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-yellow-400">{activeDeals}</p>
            <p className="text-xs text-surface-500 mt-1">Active Deals</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-green-400">{stats?.month?.deals_closed ?? 0}</p>
            <p className="text-xs text-surface-500 mt-1">Closed (30d)</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-purple-400">{custStats?.total ?? 0}</p>
            <p className="text-xs text-surface-500 mt-1">Customers</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 mb-8">
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-brand-400">{total}</p>
            <p className="text-xs text-surface-500 mt-1">My Listings</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-green-400">{vehicles.filter((v: any) => v.status === 'active').length}</p>
            <p className="text-xs text-surface-500 mt-1">Active</p>
          </div>
        </div>
      )}

      {/* Revenue Card (dealer only) */}
      {dealer && stats?.month && (
        <div className="card p-6 mb-8">
          <h3 className="text-sm font-medium text-surface-500 mb-3">Last 30 Days</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xl font-bold text-green-400">{fmt(stats.month.revenue_cents)}</p>
              <p className="text-xs text-surface-500">Revenue</p>
            </div>
            <div>
              <p className="text-xl font-bold text-brand-400">{fmt(stats.month.gross_cents)}</p>
              <p className="text-xs text-surface-500">Gross Profit</p>
            </div>
            <div>
              <p className="text-xl font-bold text-surface-300">{stats.avg_deal_days || '-'}</p>
              <p className="text-xs text-surface-500">Avg Days to Close</p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className={`grid ${dealer ? 'grid-cols-3' : 'grid-cols-1'} gap-3 mb-8`}>
        <Link
          to="/add"
          className="card p-6 text-center border-dashed border-2 border-surface-600 hover:border-brand-500 transition-colors"
        >
          <span className="text-3xl block mb-1">+</span>
          <span className="text-sm font-semibold">{dealer ? 'Add Vehicle' : 'Sell a Car'}</span>
        </Link>
        {dealer && (
          <Link
            to="/auction"
            className="card p-6 text-center border-dashed border-2 border-surface-600 hover:border-green-500 transition-colors"
          >
            <span className="text-3xl block mb-1">🔍</span>
            <span className="text-sm font-semibold">Auction Scan</span>
          </Link>
        )}
        {dealer && (
          <Link
            to="/deals"
            className="card p-6 text-center border-dashed border-2 border-surface-600 hover:border-brand-500 transition-colors"
          >
            <span className="text-3xl block mb-1">$</span>
            <span className="text-sm font-semibold">New Deal</span>
          </Link>
        )}
      </div>

      {/* Recent Inventory */}
      {vehicles.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">{dealer ? 'Recent Inventory' : 'My Vehicles'}</h3>
            <Link to="/inventory" className="text-brand-400 text-sm">View all</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {vehicles.map((v: any) => (
              <VehicleCard key={v.id} vehicle={v} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
